import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';

@Component({
  selector: 'app-trademark-notice',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  template: `
    <div class="min-h-screen bg-polished-charcoal text-silk-white py-12 px-4 sm:px-6 lg:px-8">
      <div class="max-w-4xl mx-auto space-y-8">
        
        <!-- Barra de Navegación Legal Superior (Tabs UX) -->
        <div class="bg-black/40 backdrop-blur-md p-2 rounded-2xl border border-white/10 flex flex-wrap gap-2 justify-center sm:justify-between items-center text-xs sm:text-sm">
          <a routerLink="/" class="text-amethyst-glint hover:text-white px-3 py-2 rounded-xl transition flex items-center gap-1.5 font-medium">
            ← Inicio
          </a>
          <div class="flex flex-wrap gap-1 sm:gap-2">
            <a routerLink="/terminos" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              ⚖️ Términos
            </a>
            <a routerLink="/privacidad" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              🔒 Privacidad
            </a>
            <a routerLink="/garantias-retracto" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              🛡️ Garantías
            </a>
            <a routerLink="/aviso-legal" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              🏛️ Marcas
            </a>
          </div>
          <button (click)="printPage()" class="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 text-silk-white/80 hover:text-white transition text-xs border border-white/10">
            🖨️ Imprimir
          </button>
        </div>

        <!-- Tarjeta Principal de Contenido -->
        <div class="bg-black/30 backdrop-blur-md rounded-3xl shadow-2xl p-8 sm:p-12 border border-white/10">
          
          <div class="flex items-center justify-between border-b border-white/10 pb-6 mb-8">
            <div>
              <span class="text-xs text-amethyst-glint uppercase tracking-widest font-bold">Propiedad Industrial • Colombia</span>
              <h1 class="text-3xl sm:text-4xl font-extrabold text-white mt-1 tracking-tight">
                Aviso Legal y Descargo de Responsabilidad de Marcas
              </h1>
            </div>
            <div class="text-right hidden sm:block">
              <p class="text-xs text-silk-white/50">Actualización</p>
              <p class="text-sm font-semibold text-amethyst-glint">Noviembre 2026</p>
            </div>
          </div>

          <p class="text-sm text-silk-white/60 mb-8 bg-white/5 p-4 rounded-xl border border-white/5">
            🏛️ Doctrina de Uso Descriptivo y Normativa sobre Propiedad Intelectual en la República de Colombia.
          </p>

          <div class="space-y-8 text-silk-white/90 leading-relaxed text-sm sm:text-base">
            
            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>1.</span> Carácter Independiente del Establecimiento
              </h2>
              <p class="text-silk-white/80">
                <strong>Altair Parfum</strong> (<a href="https://altairparfum.com" class="text-cyan-400 underline">altairparfum.com</a>) opera como un establecimiento comercial independiente en Bogotá, Colombia, especializado en la importación de perfumería árabe original y servicios de fraccionamiento artesanal (decants de 5ml y 10ml) y botellas completas.
              </p>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>2.</span> Uso Descriptivo y Nominativo de Marcas Registradas
              </h2>
              <p class="text-silk-white/80">
                Las marcas comerciales, logotipos y nombres de casas perfumeristas (ej. <em>Lattafa, Maison Alhambra, Afnan, Armaf, Rasasi, Al Haramain</em>) pertenecen en exclusiva a sus respectivos titulares. Su mención cumple un propósito <strong>estrictamente descriptivo e informativo</strong> para identificar la fragancia original contenida en nuestros viales fraccionados.
              </p>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>3.</span> Ausencia de Afiliación o Patrocinio Oficial
              </h2>
              <p class="text-silk-white/80">
                Altair Parfum <strong>no es un distribuidor oficial, licenciatario ni posee afiliación corporativa</strong> con los fabricantes de las marcas mencionadas. Garantizamos absoluta autenticidad del líquido dispensado, obtenido de botellas originales adquiridas legítimamente.
              </p>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>4.</span> Proceso de Fraccionamiento (Decanting)
              </h2>
              <p class="text-silk-white/80">
                El fraccionamiento se realiza de forma artesanal e higiénica bajo pedido, sin alterar la formulación química o esencia original de la fragancia.
              </p>
            </section>

          </div>

          <div class="mt-12 pt-6 border-t border-white/15 flex justify-between items-center">
            <a routerLink="/garantias-retracto" class="text-amethyst-glint hover:text-white text-sm transition">
              ← Anterior: Garantías y Retracto
            </a>
            <a routerLink="/tienda" class="bg-violet-600 hover:bg-violet-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg transition">
              Explorar Catálogo
            </a>
          </div>

        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrademarkNoticeComponent {
  printPage() {
    window.print();
  }
}
